import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Format date for display
export function formatDate(dateString, locale = 'fr-FR') {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Format date and time
export function formatDateTime(dateString, locale = 'fr-FR') {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Format relative time
export function formatRelativeTime(dateString, locale = 'fr-FR') {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return locale === 'fr-FR' ? 'À l\'instant' : 'Just now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return formatDate(dateString, locale);
}

// Truncate text
export function truncate(text, maxLength = 100) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// Get initials from name
export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Get role display name
export function getRoleDisplay(role, lang = 'fr') {
  const roles = {
    fr: {
      owner: 'Maître du Jeu',
      editor: 'Co-MJ',
      member: 'Joueur',
      readonly: 'Spectateur'
    },
    en: {
      owner: 'Game Master',
      editor: 'Co-GM',
      member: 'Player',
      readonly: 'Spectator'
    }
  };
  return roles[lang]?.[role] || role;
}

// Get quest status color class
export function getQuestStatusClass(status) {
  const classes = {
    unknown: 'status-unknown',
    known: 'status-known',
    in_progress: 'status-in_progress',
    completed: 'status-completed',
    failed: 'status-failed',
    abandoned: 'status-abandoned'
  };
  return classes[status] || 'status-unknown';
}

// Get NPC status color class
export function getNPCStatusClass(status) {
  const classes = {
    alive: 'npc-alive',
    dead: 'npc-dead',
    missing: 'npc-missing',
    hostile: 'npc-hostile',
    ally: 'npc-ally'
  };
  return classes[status] || 'npc-alive';
}

// Get visibility badge class
export function getVisibilityClass(visibility) {
  const classes = {
    mj_only: 'visibility-mj_only',
    players: 'visibility-players',
    custom: 'visibility-custom'
  };
  return classes[visibility] || 'visibility-mj_only';
}

// Check if user is GM
export function isGameMaster(role) {
  return role === 'owner' || role === 'editor';
}

// Generate random color for avatars
export function getAvatarColor(id) {
  const colors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-amber-500',
    'bg-yellow-500',
    'bg-lime-500',
    'bg-green-500',
    'bg-emerald-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-sky-500',
    'bg-blue-500',
    'bg-indigo-500',
    'bg-violet-500',
    'bg-purple-500',
    'bg-fuchsia-500',
    'bg-pink-500',
    'bg-rose-500'
  ];
  
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}
